import React from "react";
import cx from "classnames";
import {
  Button,
  ClockIcon,
  DangerIcon,
  InfoIcon,
  LocationBlockIcon,
  LockIcon,
  TickCircleIcon as SuccessIcon,
  WarningIcon,
} from "@picklebet/ui";
import { triggerHaptic } from "@/utilities/haptics";
import type { Alert as AlertType } from "@/features/betslip/types";

import * as styles from "@/features/betslip/components/parts/SlipAlert.module.scss";

const leadIcon = (alert: AlertType) => {
  switch (alert.icon) {
    case "clock":
      return <ClockIcon />;
    case "lock":
      return <LockIcon />;
    case "location":
      return <LocationBlockIcon />;
  }
  switch (alert.variant) {
    case "warning":
      return <WarningIcon />;
    case "success":
      return <SuccessIcon />;
    case "info":
      return <InfoIcon />;
    default:
      return <DangerIcon />;
  }
};

type SlipAlertProps = {
  alert: AlertType;
  onCta?: (cta: NonNullable<AlertType["cta"]>) => void;
};

// Button has no `success` variant; map the alert's success to the brand
// primary. danger/warning/info line up one-to-one.
const ctaVariant = (variant: AlertType["variant"]) =>
  variant === "success" ? "primary" : variant;

// Slip-level alert (transport errors, account state). Vertical layout per the
// Figma: top-aligned icon, message, then a full CTA button beneath. Distinct
// from the card-level InlineAlert (horizontal, with a tail).
export const SlipAlert = ({ alert, onCta }: SlipAlertProps) => {
  const handleClick = () => {
    if (!alert.cta || !onCta) return;
    triggerHaptic.click();
    onCta(alert.cta);
  };

  return (
    <div
      className={cx(styles.alert, styles[alert.variant], {
        [styles.topAligned]: !!alert.cta,
      })}
      role="alert"
      data-variant={alert.variant}
    >
      <span className={styles.icon}>{leadIcon(alert)}</span>
      <div className={styles.content}>
        <p className={styles.message} data-testid="alert-message">
          {alert.message}
        </p>
        {alert.cta && (
          <Button
            variant={ctaVariant(alert.variant)}
            size="xs"
            onClick={handleClick}
          >
            {alert.cta.label}
          </Button>
        )}
      </div>
    </div>
  );
};
